import { Box, Chip, IconButton, Stack, Typography } from '@mui/material'
import type { CartRowModel, CounterRules } from '@commerce/core'
import { YALO_RADIUS, YALO_SEMANTIC, YALO_SHADOW } from '@commerce/tokens'
import { QuantityStepper } from './QuantityStepper'

interface Props {
  model: CartRowModel
  rules: CounterRules
  onQuantityChange: (sku: string, quantity: number) => void
  onRemove: (sku: string) => void
}

/**
 * One cart line, as the design draws it: a card with the product's thumbnail on
 * a white tile, the unit price under the name, and the line total sharing a row
 * with the stepper and the remove control.
 *
 * Like `CatalogCard`, it does no arithmetic and no formatting: `unitPriceText`
 * and `lineTotalText` were resolved by `toCartRowModel`, and the quantity rules
 * by `counterMachine`.
 *
 * A freegood renders without a remove control and without a stepper. The shopper
 * did not add it — a promotion did — so offering either would be offering an
 * action the backend will refuse.
 *
 * TODO(post-hackathon): the aria-labels here and in `QuantityStepper` are
 * English while the visible copy is Spanish. Both should come from
 * `config.texts` so a storefront's assistive labels match its language.
 */
export const CartRow = ({ model, rules, onQuantityChange, onRemove }: Props) => (
  <Stack
    direction="row"
    spacing={1.5}
    sx={{
      bgcolor: YALO_SEMANTIC.bgSurface,
      border: `1px solid ${YALO_SEMANTIC.borderSubtle}`,
      borderRadius: `${YALO_RADIUS.lg}px`,
      boxShadow: YALO_SHADOW.xs,
      p: 1.5,
    }}
    data-testid={`cart-row-${model.sku}`}
  >
    <Box
      sx={{
        width: 58,
        height: 58,
        flexShrink: 0,
        borderRadius: `${YALO_RADIUS.md}px`,
        border: `1px solid ${YALO_SEMANTIC.borderSubtle}`,
        bgcolor: YALO_SEMANTIC.bgSurface,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box
        component="img"
        src={model.imageUrl}
        alt={model.title}
        sx={{ width: '82%', height: '82%', objectFit: 'contain' }}
      />
    </Box>

    <Stack flex={1} spacing={0.75} sx={{ minWidth: 0 }}>
      <Typography variant="body2" fontWeight={600}>
        {model.title}
      </Typography>

      {model.promotionBadge && (
        <Box>
          <Chip size="small" color="secondary" label={model.promotionBadge.text} />
        </Box>
      )}

      <Typography variant="caption" color="text.secondary">
        Precio{' '}
        <Box component="span" sx={{ fontWeight: 600, color: 'primary.dark' }}>
          {model.unitPriceText}
        </Box>
      </Typography>

      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ color: 'primary.dark' }}>
          {model.lineTotalText}
        </Typography>

        {model.isFreegood ? (
          <Typography variant="body2" color="text.secondary">
            {model.quantity} de regalo
          </Typography>
        ) : (
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <QuantityStepper
              value={model.quantity}
              rules={rules}
              onCommit={(quantity) => onQuantityChange(model.sku, quantity)}
            />
            {model.canRemove && (
              <IconButton
                aria-label={`remove ${model.title}`}
                onClick={() => onRemove(model.sku)}
                sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}
              >
                {/* The design's trash glyph, inline rather than an icon package:
                    `react-icons` is one of the dependencies this port dropped. */}
                <Box
                  component="svg"
                  aria-hidden
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1.8}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  sx={{ width: 18, height: 18 }}
                >
                  <path d="M4 7h16M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M6.5 7l1 13a1 1 0 0 0 1 .9h7a1 1 0 0 0 1-.9l1-13M10 11v6M14 11v6" />
                </Box>
              </IconButton>
            )}
          </Stack>
        )}
      </Stack>
    </Stack>
  </Stack>
)
